import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  ArrowRight,
  ChevronDown,
  Flame,
  Gauge,
  Layers3,
  MapPin,
  Menu,
  Phone,
  X,
  type LucideIcon,
} from "lucide-react";
import { CONTACT, IMAGES } from "@/lib/constants";
import AwardSeal from "./AwardSeal";

interface NavChild {
  label: string;
  href: string;
  description?: string;
  icon?: LucideIcon;
}

interface NavItem {
  label: string;
  href: string;
  children?: NavChild[];
}

const navItems: NavItem[] = [
  {
    label: "Tjänster",
    href: "/tjanster",
    children: [
      {
        label: "Laserskärning",
        href: "/tjanster/laserskarning",
        description: "Precision ner till 0,1 mm i stål, rostfritt och aluminium.",
        icon: Gauge,
      },
      {
        label: "Kantpressning",
        href: "/tjanster/kantpressning",
        description: "Exakt bockning med presskraft upp till 310 ton.",
        icon: Layers3,
      },
      {
        label: "Svetsning",
        href: "/tjanster/svetsning",
        description: "Kompletta svetsade komponenter för industrin.",
        icon: Flame,
      },
    ],
  },
  { label: "Maskinpark", href: "/maskinpark" },
  { label: "Om oss", href: "/om-oss" },
  {
    label: "Referenser",
    href: "/referenser",
    children: [
      { label: "Volvo", href: "/referenser/volvo" },
      { label: "Försvarsindustrin", href: "/referenser/forsvarsindustrin" },
    ],
  },
  {
    label: "Kunskapsbank",
    href: "/nyheter",
    children: [
      { label: "Laserskärning", href: "/kunskapsbank/laserskarning" },
      { label: "Kantpressning", href: "/kunskapsbank/kantpressning" },
      { label: "Svetsning", href: "/kunskapsbank/svetsning" },
      { label: "Material & Stål", href: "/kunskapsbank/material" },
      { label: "Branschlösningar", href: "/kunskapsbank/branscher" },
      { label: "Guider & Processer", href: "/kunskapsbank/guider" },
      { label: "Framtid & Innovation", href: "/kunskapsbank/framtid" },
    ],
  },
  { label: "Kontakt", href: "/kontakt" },
];

const sectionId = (label: string) =>
  `mobile-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

function isNavItemActive(item: NavItem, location: string) {
  if (item.label === "Tjänster") {
    return location.startsWith("/tjanster") || location.startsWith("/laserskarning-");
  }
  return location.startsWith(item.href);
}

function isChildActive(child: NavChild, location: string) {
  if (child.href === "/tjanster/laserskarning") {
    return location === child.href || location.startsWith("/laserskarning-");
  }
  return location === child.href;
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [location] = useLocation();
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const isHomepage = location === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileSection(null);
    setOpenDropdown(null);
  }, [location]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenDropdown(null);
        setMobileOpen(false);
        setMobileSection(null);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const headerBg = scrolled
    ? "bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm"
    : isHomepage
      ? "bg-transparent"
      : "bg-white";

  const textColor = scrolled || !isHomepage ? "text-gray-600" : "text-white/90";
  const textColorActive = scrolled || !isHomepage ? "text-[#2563EB]" : "text-white";
  const textColorHover = scrolled || !isHomepage ? "hover:text-gray-900" : "hover:text-white";
  const logoOpacity = scrolled || !isHomepage ? "" : "brightness-0 invert";

  const toggleMobileMenu = () => {
    const nextOpen = !mobileOpen;
    setMobileOpen(nextOpen);
    if (!nextOpen) {
      setMobileSection(null);
      return;
    }

    const activeSection = navItems.find(
      item => item.children && isNavItemActive(item, location),
    );
    setMobileSection(activeSection?.label ?? null);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
    >
      {/* Desktop trust bar — inspired by Nordiska Inglasningar's header placement */}
      <div
        className={`hidden transition-[max-height,opacity,border-color] duration-300 xl:block ${
          scrolled
            ? "max-h-0 overflow-hidden border-transparent opacity-0"
            : "max-h-14 overflow-visible border-b border-gray-200 bg-white opacity-100"
        }`}
      >
        <div className="container flex h-14 items-center justify-between">
          <p className="flex items-center gap-2 text-xs font-medium tracking-wide text-gray-500">
            <span className="h-1.5 w-1.5 rounded-full bg-[#2563EB]" aria-hidden="true" />
            Metallbearbetning i Eskilstuna sedan 1993
          </p>
          <div className="flex items-center gap-4" aria-label="ASPAB:s utmärkelser">
            <AwardSeal
              variant="light"
              placement="below"
              tooltipId="branschvinnare-tooltip-header"
              imageClassName="h-12 w-auto transition-transform duration-200 hover:scale-105"
              width={113}
              height={48}
              priority
            />
            <span className="h-8 w-px bg-gray-200" aria-hidden="true" />
            <img
              src={IMAGES.kreditvardighet}
              alt="Högsta kreditvärdighet AAA – Dun & Bradstreet"
              className="h-10 w-auto"
              width={76}
              height={40}
              loading="eager"
              fetchPriority="high"
            />
          </div>
        </div>
      </div>

      <div className="container flex h-16 items-center justify-between xl:h-20">
        <Link href="/" className="flex shrink-0 items-center group">
          <img
            src={IMAGES.logo}
            alt="ASPAB laserskärning logotyp"
            className={`h-10 w-auto transition-all group-hover:opacity-80 md:h-12 ${logoOpacity}`}
            loading="eager"
            width={180}
            height={48}
          />
        </Link>

        <nav aria-label="Huvudnavigering" className="hidden items-center gap-0.5 lg:flex">
          {navItems.map(item => {
            const active = isNavItemActive(item, location);
            const isOpen = openDropdown === item.label;

            return (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && setOpenDropdown(item.label)}
                onMouseLeave={() => setOpenDropdown(null)}
                onFocusCapture={() => item.children && setOpenDropdown(item.label)}
                onBlurCapture={event => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                    setOpenDropdown(null);
                  }
                }}
              >
                <Link
                  href={item.href}
                  aria-haspopup={item.children ? "true" : undefined}
                  aria-expanded={item.children ? isOpen : undefined}
                  className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    active ? textColorActive : `${textColor} ${textColorHover}`
                  }`}
                >
                  {item.label}
                  {item.children && (
                    <ChevronDown
                      className={`mt-0.5 h-3.5 w-3.5 transition-transform duration-200 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </Link>

                <div
                  className={`absolute left-0 top-full pt-3 transition-all duration-200 ${
                    item.children && isOpen
                      ? "pointer-events-auto translate-y-0 opacity-100"
                      : "pointer-events-none -translate-y-1 opacity-0"
                  }`}
                >
                  {item.children && item.label === "Tjänster" ? (
                    <div className="w-[390px] overflow-hidden rounded-xl border border-gray-200 bg-white p-2 shadow-2xl shadow-gray-900/15">
                      <div className="px-3 pb-2 pt-3">
                        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#2563EB]">
                          Våra tjänster
                        </span>
                        <p className="mt-1 text-sm text-gray-500">
                          Från ritning till färdig komponent.
                        </p>
                      </div>
                      <div className="space-y-1">
                        {item.children.map(child => {
                          const Icon = child.icon;
                          const childActive = isChildActive(child, location);
                          return (
                            <Link
                              key={child.href}
                              href={child.href}
                              className={`group/item flex items-center gap-3 rounded-lg px-3 py-3 transition-colors ${
                                childActive
                                  ? "bg-blue-50 text-[#2563EB]"
                                  : "text-gray-700 hover:bg-gray-50 hover:text-gray-950"
                              }`}
                            >
                              <span
                                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                                  childActive
                                    ? "bg-[#2563EB] text-white"
                                    : "bg-gray-100 text-[#2563EB] group-hover/item:bg-blue-50"
                                }`}
                              >
                                {Icon && <Icon className="h-5 w-5" />}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block text-sm font-semibold">{child.label}</span>
                                <span className="mt-0.5 block text-xs leading-relaxed text-gray-500">
                                  {child.description}
                                </span>
                              </span>
                              <ArrowRight className="h-4 w-4 shrink-0 text-gray-300 transition-transform group-hover/item:translate-x-0.5 group-hover/item:text-[#2563EB]" />
                            </Link>
                          );
                        })}
                      </div>
                      <div className="mt-2 border-t border-gray-100 p-2">
                        <a
                          href="/tjanster/laserskarning#leveransomraden"
                          className="flex items-center justify-between rounded-lg bg-gray-950 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
                        >
                          <span className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-[#60A5FA]" />
                            Laserskärning nära dig
                          </span>
                          <ArrowRight className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  ) : item.children ? (
                    <div className="min-w-[240px] overflow-hidden rounded-xl border border-gray-200 bg-white p-2 shadow-xl shadow-gray-900/10">
                      {item.children.map(child => (
                        <Link
                          key={child.href}
                          href={child.href}
                          className={`block rounded-lg px-4 py-2.5 text-sm transition-colors ${
                            isChildActive(child, location)
                              ? "bg-blue-50 font-medium text-[#2563EB]"
                              : "text-gray-600 hover:bg-gray-50 hover:text-gray-950"
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="flex shrink-0 items-center gap-3">
          <a
            href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}
            className={`hidden items-center gap-2 whitespace-nowrap text-sm transition-colors 2xl:flex ${
              scrolled || !isHomepage
                ? "text-gray-500 hover:text-[#2563EB]"
                : "text-white/80 hover:text-white"
            }`}
          >
            <Phone className="h-4 w-4" />
            {CONTACT.phone}
          </a>
          <Link
            href="/kontakt"
            className="hidden items-center whitespace-nowrap rounded-md bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8] sm:inline-flex"
          >
            Begär offert
          </Link>
          <button
            type="button"
            onClick={toggleMobileMenu}
            className={`flex h-11 w-11 items-center justify-center transition-colors lg:hidden ${
              scrolled || !isHomepage
                ? "text-gray-600 hover:text-gray-900"
                : "text-white hover:text-white/80"
            }`}
            aria-label={mobileOpen ? "Stäng meny" : "Öppna meny"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <div
        id="mobile-navigation"
        ref={mobileMenuRef}
        className={`overflow-y-auto border-t border-gray-200 bg-white transition-all duration-300 ease-in-out lg:hidden ${
          mobileOpen ? "max-h-[calc(100vh-4rem)] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav aria-label="Mobilnavigering" className="container space-y-1 py-4">
          {navItems.map(item => {
            const active = isNavItemActive(item, location);
            const expanded = mobileSection === item.label;
            const panelId = sectionId(item.label);

            return (
              <div key={item.label}>
                <div className={`flex items-center rounded-lg ${active ? "bg-blue-50" : ""}`}>
                  <Link
                    href={item.href}
                    className={`min-w-0 flex-1 px-4 py-3 text-base font-medium transition-colors ${
                      active ? "text-[#2563EB]" : "text-gray-700 hover:text-gray-950"
                    }`}
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <button
                      type="button"
                      onClick={() => setMobileSection(expanded ? null : item.label)}
                      className="mr-1 flex h-11 w-11 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-white hover:text-gray-950"
                      aria-label={`${expanded ? "Dölj" : "Visa"} undermeny för ${item.label}`}
                      aria-expanded={expanded}
                      aria-controls={panelId}
                    >
                      <ChevronDown
                        className={`h-5 w-5 transition-transform duration-200 ${
                          expanded ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  )}
                </div>

                {item.children && (
                  <div
                    id={panelId}
                    className={`grid transition-[grid-template-rows,opacity] duration-300 ${
                      expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="mb-2 ml-4 mt-1 space-y-1 border-l border-gray-200 pl-4">
                        {item.children.map(child => {
                          const Icon = child.icon;
                          return (
                            <Link
                              key={child.href}
                              href={child.href}
                              className={`flex items-center gap-3 rounded-md px-3 py-3 text-sm transition-colors ${
                                isChildActive(child, location)
                                  ? "bg-blue-50 font-medium text-[#2563EB]"
                                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-950"
                              }`}
                            >
                              {Icon && <Icon className="h-4 w-4 shrink-0 text-[#2563EB]" />}
                              {child.label}
                            </Link>
                          );
                        })}
                        {item.label === "Tjänster" && (
                          <a
                            href="/tjanster/laserskarning#leveransomraden"
                            className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-[#2563EB] transition-colors hover:bg-blue-50"
                          >
                            <MapPin className="h-4 w-4 shrink-0" />
                            Laserskärning nära dig
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div className="mt-4 border-t border-gray-200 pt-4">
            <a
              href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}
              className="mb-3 flex items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-[#2563EB]"
            >
              <Phone className="h-4 w-4" />
              {CONTACT.phone}
            </a>
            <Link
              href="/kontakt"
              className="block rounded-md bg-[#2563EB] px-5 py-3 text-center font-semibold text-white transition-colors hover:bg-[#1D4ED8]"
            >
              Begär offert
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
}
